import apiService from './api';

export interface CartItem {
  id: number;
  userId: number;
  productId: number;
  quantity: number;
  createdAt: Date;
  updatedAt: Date;
  product?: {
    id: number;
    name: string;
    price: number;
    discountPrice?: number;
    imageUrl?: string;
    stock: number;
    available: boolean;
  };
}

export interface AddToCartData {
  productId: number;
  quantity: number;
}

export interface UpdateCartItemData {
  quantity: number;
}

class CartService {
  async getCartItems(): Promise<CartItem[]> {
    try {
      const response = await apiService.get<CartItem[]>('/cart/getCartItems');
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch cart items');
    }
  }

  async addToCart(data: AddToCartData): Promise<CartItem> {
    try {
      // First check if product is available and has sufficient stock
      const productResponse = await apiService.get<{ id: number; stock: number; available: boolean }>(`/products/getProductById/${data.productId}`);

      if (!productResponse.available) {
        throw new Error('Product is currently out of stock');
      }

      if (productResponse.stock < data.quantity) {
        throw new Error(`Only ${productResponse.stock} items available in stock`);
      }

      const response = await apiService.post<CartItem>('/cart/addToCart', data);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to add item to cart');
    }
  }

  async updateCartItem(itemId: number, data: UpdateCartItemData): Promise<CartItem> {
    try {
      const response = await apiService.put<CartItem>(`/cart/updateCartItemQuantity/${itemId}`, data);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update cart item');
    }
  }

  async removeFromCart(itemId: number): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.delete<{ success: boolean; message: string }>(`/cart/removeFromCart/${itemId}`);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to remove item from cart');
    }
  }

  async clearCart(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.delete<{ success: boolean; message: string }>('/cart/clearUserCart');
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to clear cart');
    }
  }

  async getCartTotal(): Promise<{ total: number; itemCount: number }> {
    try {
      return await apiService.get<{ total: number; itemCount: number }>('/cart/getCartTotal');
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to get cart total');
    }
  }

  async moveToWishlist(itemId: number): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.post<{ success: boolean; message: string }>(`/cart/moveToWishlist/${itemId}`);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to move item to wishlist');
    }
  }

  async validateCartItemStock(itemId: number): Promise<{ isValid: boolean; currentStock: number; available: boolean }> {
    try {
      // Get cart item details to check product stock
      const cartItem = await this.getCartItems();
      const item = cartItem.find(cartItem => cartItem.id === itemId);

      if (!item) {
        throw new Error('Cart item not found');
      }

      // Check current product stock
      const productResponse = await apiService.get<{ id: number; stock: number; available: boolean }>(`/products/getProductById/${item.productId}`);

      return {
        isValid: productResponse.available && productResponse.stock >= item.quantity,
        currentStock: productResponse.stock,
        available: productResponse.available
      };
    } catch (error) {
      return {
        isValid: false,
        currentStock: 0,
        available: false
      };
    }
  }
}

export const cartService = new CartService();
export default cartService;
